import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Search, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { KIND_SHORT, describeParams, fmt, fmtTime } from "../preview/meta";
import { UndoDialog } from "../preview/UndoDialog";
import { ResultCounts, TypeBadge, byKind } from "./ApplyHistory";
import { DetailRows } from "./DetailRows";

const PAGE = 100;
const FILTERS = [["", "All"], ["applied", "Applied"], ["skipped", "Skipped"], ["failed", "Failed"], ["undone", "Undone"]];

function Header({ a, onUndoAll, canUndo, undoable }) {
  return (
    <Card className="border-slate-200 px-5 py-4 shadow-sm" data-testid="bsh-detail-header">
      <Link to="/billing-sync/apply-history" className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="bsh-back"><ArrowLeft className="h-3.5 w-3.5" /> Apply History</Link>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <TypeBadge type={a.type} />
        <h3 className="font-heading text-base font-semibold text-slate-800">{fmtTime(a.started_at)} · {a.started_by_name}</h3>
        <ResultCounts c={a.counts} />
        {a.type !== "undo" && canUndo && a.status !== "running" && undoable > 0 && (
          <Button size="sm" variant="outline" className="ml-auto" onClick={onUndoAll} data-testid="bsh-undo-all"><Undo2 className="h-3.5 w-3.5" /> Undo everything applied</Button>)}
      </div>
      <p className="mt-1 text-xs text-slate-500">{byKind(a.by_kind)} · {a.type === "undo" ? `undo of ${(a.of_applies || []).join(", ")}` : a.run_params ? describeParams(a.run_params) : `run ${a.run_id}`}
        {a.status === "running" ? ` · running ${a.done}/${a.total}` : ` · ${a.status}${a.finished_at ? ` ${fmtTime(a.finished_at)}` : ""}`}{a.email ? ` · e-mail ${a.email.status}` : ""}</p>
      {a.error && <p className="mt-1 text-xs text-red-700" data-testid="bsh-detail-error">{a.error}</p>}
    </Card>
  );
}

export function ApplyDetail() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [f, setF] = usePersistedState("apply-detail.f", { status: "", kind: "", q: "" });
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const [undo, setUndo] = useState(null);
  const [tick, setTick] = useState(0);
  const close = useCallback(() => setUndo(null), []);
  useEffect(() => { api.get(`/billing-sync/preview/applies/${aid}`).then(({ data: d }) => setA(d)).catch(() => toast.error("Apply not found")); }, [aid, tick]);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/billing-sync/applies/${aid}/rows`, { params: { ...f, skip, limit: PAGE } }).then(({ data: d }) => setData(d)).catch(() => {}), f.q ? 250 : 0);
    return () => clearTimeout(t);
  }, [aid, f, skip, tick]);
  useEffect(() => {
    if (a?.status !== "running") return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 3000);
    return () => clearInterval(t);
  }, [a?.status]);
  if (!a) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-slate-300" />;
  const set = (k, v) => { setSkip(0); setF({ ...f, [k]: v }); };
  return (
    <div className="space-y-4" data-testid="bsh-detail">
      <Header a={a} canUndo={a.can_apply} undoable={(data?.counts?.applied || 0) - (data?.counts?.undone || 0)} onUndoAll={() => setUndo({ apply_id: a.id })} />
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex gap-1">{FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => set("status", k)} data-testid={`bsh-filter-${k || "all"}`}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${f.status === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {l}{k && data?.counts ? ` ${fmt(data.counts[k] || 0)}` : ""}</button>))}</div>
          <select value={f.kind} onChange={(e) => set("kind", e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="bsh-kind">
            <option value="">All changes</option>{Object.entries(KIND_SHORT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <div className="relative ml-auto w-64"><Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
            <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search domain, item, result…" className="h-8 pl-8 text-xs" data-testid="bsh-search" /></div>
        </div>
        <div className="max-h-[65vh] overflow-auto">
          {!data ? <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-300" /> : data.rows.length ? (
            <DetailRows rows={data.rows} isUndoJob={a.type === "undo"} canUndo={a.can_apply && a.status !== "running"} onUndo={(items) => setUndo({ items })} />
          ) : <p className="p-10 text-center text-sm text-slate-500" data-testid="bsh-rows-empty">No rows{f.status || f.kind || f.q ? " match the filter" : " were recorded for this job"}.</p>}
        </div>
        {data && data.total > PAGE && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
            {fmt(skip + 1)}–{fmt(Math.min(skip + PAGE, data.total))} of {fmt(data.total)}
            <Button size="sm" variant="ghost" disabled={!skip} onClick={() => setSkip(Math.max(0, skip - PAGE))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)}><ChevronRight className="h-4 w-4" /></Button>
          </div>)}
      </Card>
      {undo && <UndoDialog target={undo} onClose={close} onStarted={(id) => { setUndo(null); navigate(`/billing-sync/apply-history/${id}`); }} />}
    </div>
  );
}
